import { CommonModule } from "@angular/common";
import { Component, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25LoadingInlineModule } from "../../modules/s25-loading/s25.loading.inline.module";
import {
    ColorScheme,
    ItemColor,
    ItemColorErrors,
    ItemColorMappingService,
} from "../../services/item.color.mapping.service";
import { S25InfoMessageModule } from "../../modules/s25-info-message/s25.info.message.module";

interface EditableItemColor extends ItemColor {
    inUse?: boolean;
    isNew?: boolean;
    isRemoved?: boolean;
    errors: ItemColorErrors;
}

interface SaveColorSchemePayload {
    is_new?: boolean;
    is_removed?: boolean;
    scheme_id?: number;
    color_hash: string;
    text_color: string;
    scheme_name: string;
    pattern: string | null;
}

interface SavedColorScheme {
    scheme_id: number;
    scheme_name: string;
}

interface SaveColorSchemeResponse {
    schemes?: SavedColorScheme[];
}

interface RgbColor {
    red: number;
    green: number;
    blue: number;
}

@TypeManagerDecorator("s25-ng-item-color-custom")
@Component({
    selector: "s25-ng-item-color-custom",
    standalone: true,
    imports: [CommonModule, FormsModule, S25InfoMessageModule, S25LoadingInlineModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
        <h2 class="c-margin-bottom--single">Grid Color Schemes</h2>
        @if (!isInit()) {
            <s25-loading-inline [model]="{}"></s25-loading-inline>
        } @else {
            <div>
                <button class="aw-button aw-button--outline c-margin-bottom--single" (click)="addScheme()">
                    Add New Color Scheme
                </button>
            </div>
            @if (message()) {
                <div
                    [class]="
                        (hasErrors() ? 'ngRed cn-alert--danger' : 'ngGreen cn-alert--success') +
                        ' ngBold cn-alert c-margin-bottom--single'
                    "
                    role="alert"
                >
                    <div class="cn-alert__label">
                        <span>{{ message() }}</span>
                    </div>
                </div>
            }

            @for (scheme of customColors(); track scheme.id ?? $index) {
                <div
                    class="grid-color-card--wrapper"
                    [class.errors]="!scheme.errors.noErrors"
                    [class.inUse]="!!scheme.inUse"
                >
                    <div class="grid-color-card--content">
                        @if (scheme.inUse) {
                            <span>
                                Color Scheme Currently In Use
                                <s25-ng-info-message [customLabel]="'Color Scheme in Use'" [isPopover]="true">
                                    {{ schemeInUseInfo }}
                                </s25-ng-info-message>
                            </span>
                        }

                        <div class="grid-color-card--flex-row">
                            <label [for]="'colorSchemeName' + $index">Color Scheme Name:</label>
                            <input
                                [id]="'colorSchemeName' + $index"
                                type="text"
                                name="schemeName"
                                [(ngModel)]="scheme.name"
                                class="c-input"
                                [class.input-error]="scheme.errors.showError1 || scheme.errors.showError2"
                                [readonly]="!!scheme.inUse"
                                (ngModelChange)="setName(scheme)"
                            />
                        </div>
                        @if (scheme.errors.showError1 || scheme.errors.showError2) {
                            <p role="status" class="ngRed">
                                {{
                                    scheme.errors.showError1 ? scheme.errors.errorMessage1 : scheme.errors.errorMessage2
                                }}
                            </p>
                        }

                        <div class="grid-color-card--flex-row">
                            <label [for]="'colorSchemeBackground' + $index">Background Color:</label>
                            <input
                                [id]="'colorSchemeBackground' + $index"
                                type="color"
                                name="hash"
                                [(ngModel)]="scheme.hash"
                                class="c-input"
                                (ngModelChange)="setHash(scheme)"
                            />
                        </div>

                        <div class="grid-color-card--flex-row">
                            <label [for]="'colorSchemeFont' + $index">Font Color:</label>
                            <input
                                [id]="'colorSchemeFont' + $index"
                                type="color"
                                name="textColor"
                                [(ngModel)]="scheme.textColor"
                                class="c-input"
                                [class.input-error]="scheme.errors.showError3"
                                (ngModelChange)="setTextColor(scheme)"
                            />
                        </div>
                        @if (scheme.errors.showError3) {
                            <p role="status" class="ngRed" [innerHTML]="scheme.errors.errorMessage3"></p>
                        }

                        <div class="grid-color-card--flex-row">
                            <label [for]="'colorSchemePattern' + $index">Pattern:</label>
                            <select [id]="'colorSchemePattern' + $index" [(ngModel)]="scheme.pattern" class="c-input">
                                <option [ngValue]="undefined">None</option>
                                @for (pattern of patterns(); track pattern.name) {
                                    <option [ngValue]="pattern">{{ pattern.name }}</option>
                                }
                            </select>
                        </div>

                        <div class="grid-color-card--flex-row">
                            <label [for]="'colorSchemePreview' + $index">Preview:</label>
                            <input
                                [id]="'colorSchemePreview' + $index"
                                readonly
                                type="text"
                                name="preview"
                                [value]="scheme.name ?? ''"
                                class="c-input"
                                [attr.style]="previewStyle(scheme)"
                            />
                        </div>

                        @if (!scheme.inUse) {
                            <button class="aw-button aw-button--danger--outline" (click)="removeScheme(scheme)">
                                Remove
                            </button>
                        }
                    </div>
                </div>
            }

            <div class="c-displayBlock c-padding-bottom--double">
                <button class="aw-button aw-button--primary c-margin-top--half" (click)="save()">Save</button>
                <button class="aw-button aw-button--outline c-margin-top--half" (click)="cancel()">Cancel</button>
            </div>
        }
    `,
})
export class ItemColorCustomComponent {
    readonly isInit = signal(false);
    readonly hasErrors = signal(false);
    readonly message = signal<string | undefined>(undefined);
    readonly customColors = signal<EditableItemColor[]>([]);
    readonly patterns = signal<ColorScheme[]>([]);
    readonly colorsInUseNames = signal<string[]>([]);
    readonly removedSchemes = signal<EditableItemColor[]>([]);

    readonly schemeInUseInfo =
        "When a color scheme is in use in Color Mapping, it cannot be removed and its name cannot be changed.";

    constructor() {
        void this.reload();
    }

    private createErrorModel(): ItemColorErrors {
        return {
            noErrors: true,
            errorMessage1: "Please assign a name to each color scheme",
            showError1: false,
            errorMessage2: "Please use a unique name for each color scheme",
            showError2: false,
            errorMessage3:
                "Please adjust your chosen colors so they have enough contrast to pass WCAG accessibility requirements. For more information and to test your colors, visit a <a href='https://webaim.org/resources/contrastchecker/' target='_blank'>color contrast checker</a>.",
            showError3: false,
        };
    }

    private async reload(): Promise<void> {
        this.isInit.set(false);
        this.message.set(undefined);
        this.hasErrors.set(false);
        this.removedSchemes.set([]);

        const [colorPatterns, colorSchemes, bucketTypesResponse] = await Promise.all([
            ItemColorMappingService.getPatterns(),
            ItemColorMappingService.getColorSchemes(),
            ItemColorMappingService.getBucketTypes(),
        ]);

        const colorsInUseNames: string[] = [];
        for (const bucketType of bucketTypesResponse?.types ?? []) {
            for (const bucket of bucketType.buckets ?? []) {
                if (bucket?.color?.name) {
                    colorsInUseNames.push(bucket.color.name);
                }
            }
        }

        this.patterns.set([...(colorPatterns ?? [])]);
        this.colorsInUseNames.set(colorsInUseNames);
        const patternByName = new Map((colorPatterns ?? []).map((pattern) => [pattern.name, pattern] as const));

        const editableSchemes = (colorSchemes ?? []).map((scheme) => {
            const normalizedTextColor = scheme.textColor === "#FFF" ? "#FFFFFF" : scheme.textColor;
            const normalizedPattern = scheme.pattern?.name ? patternByName.get(scheme.pattern.name) : undefined;
            return {
                ...scheme,
                textColor: normalizedTextColor,
                pattern: normalizedPattern,
                inUse: colorsInUseNames.includes(scheme.name),
                errors: scheme.errors ?? this.createErrorModel(),
            } as EditableItemColor;
        });

        this.customColors.set(editableSchemes);
        this.isInit.set(true);
    }

    addScheme(): void {
        this.message.set(undefined);
        const createdScheme: EditableItemColor = {
            name: "",
            hash: "#FFFFFF",
            textColor: "#000000",
            isNew: true,
            errors: this.createErrorModel(),
        };
        this.customColors.update((colors) => [createdScheme, ...colors]);
    }

    async removeScheme(scheme: EditableItemColor): Promise<void> {
        if (!scheme.isNew) {
            const usedBuckets = await ItemColorMappingService.checkSchemeUse(scheme.name ?? "");
            if (usedBuckets?.length) {
                scheme.errors.showError4 = true;
                this.customColors.update((colors) => [...colors]);
                return;
            }
            scheme.isRemoved = true;
            this.removedSchemes.update((removed) => [...removed, scheme]);
        }

        this.message.set(undefined);
        this.customColors.update((colors) => colors.filter((existing) => existing !== scheme));
    }

    private hasDuplicateName(targetScheme: EditableItemColor): boolean {
        const normalizedName = targetScheme.name?.trim().toLowerCase();
        if (!normalizedName) return false;

        for (const colorScheme of this.customColors()) {
            const comparingName = colorScheme.name?.trim().toLowerCase();
            if (!comparingName) continue;
            if (comparingName === normalizedName && colorScheme.id !== targetScheme.id) {
                return true;
            }
        }
        return false;
    }

    setName(scheme: EditableItemColor): void {
        scheme.errors.showError1 = !scheme.name;
        scheme.errors.showError2 = !!scheme.name && this.hasDuplicateName(scheme);
        this.setNoErrorFlag(scheme);
        this.customColors.update((colors) => [...colors]);
    }

    setHash(scheme: EditableItemColor): void {
        scheme.errors.showError3 = !!scheme.textColor && !this.hasValidContrastRatio(scheme);
        this.setNoErrorFlag(scheme);
        this.customColors.update((colors) => [...colors]);
    }

    setTextColor(scheme: EditableItemColor): void {
        scheme.errors.showError3 = !!scheme.hash && !this.hasValidContrastRatio(scheme);
        this.setNoErrorFlag(scheme);
        this.customColors.update((colors) => [...colors]);
    }

    private setNoErrorFlag(scheme: EditableItemColor): void {
        scheme.errors.noErrors = !scheme.errors.showError1 && !scheme.errors.showError2 && !scheme.errors.showError3;
    }

    patternCss(pattern: ColorScheme | undefined): string {
        const cssValue = pattern?.css?.trim();
        if (!cssValue) return "none";
        return cssValue.replace(/;+\s*$/, "");
    }

    previewStyle(scheme: EditableItemColor): string {
        const normalizedPatternCss = this.patternCss(scheme.pattern);
        const styleParts = [
            `background-color: ${scheme.hash} !important`,
            `color: ${scheme.textColor} !important`,
            `background-image: ${normalizedPatternCss} !important`,
        ];
        return styleParts.join("; ");
    }

    private hexToRgb(hexColor: string): RgbColor | undefined {
        const shorthandMatch = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        const fullHex = hexColor.replace(shorthandMatch, (_match, red, green, blue) => {
            return red + red + green + green + blue + blue;
        });
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
        if (!result) return undefined;
        const [, redHex = "00", greenHex = "00", blueHex = "00"] = result;
        return {
            red: parseInt(redHex, 16),
            green: parseInt(greenHex, 16),
            blue: parseInt(blueHex, 16),
        };
    }

    private getLuminance(red: number, green: number, blue: number): number {
        const transformedRgb = [red, green, blue].map((rgbValue) => {
            const scaledValue = rgbValue / 255;
            return scaledValue <= 0.03928 ? scaledValue / 12.92 : Math.pow((scaledValue + 0.055) / 1.055, 2.4);
        });
        const [redLuminance = 0, greenLuminance = 0, blueLuminance = 0] = transformedRgb;
        return redLuminance * 0.2126 + greenLuminance * 0.7152 + blueLuminance * 0.0722;
    }

    private hasValidContrastRatio(scheme: EditableItemColor): boolean {
        const backgroundRgb = this.hexToRgb(scheme.hash);
        const fontRgb = this.hexToRgb(scheme.textColor);
        if (!backgroundRgb || !fontRgb) return false;

        const backgroundLuminance = this.getLuminance(backgroundRgb.red, backgroundRgb.green, backgroundRgb.blue);
        const fontLuminance = this.getLuminance(fontRgb.red, fontRgb.green, fontRgb.blue);
        const brighter = Math.max(backgroundLuminance, fontLuminance);
        const darker = Math.min(backgroundLuminance, fontLuminance);
        const ratio = (brighter + 0.05) / (darker + 0.05);
        return ratio >= 4.5;
    }

    private validate(): boolean {
        let allValid = true;
        for (const scheme of this.customColors()) {
            scheme.errors.showError1 = !scheme.name;
            scheme.errors.showError2 = !scheme.inUse && !!scheme.name && this.hasDuplicateName(scheme);
            scheme.errors.showError3 = !this.hasValidContrastRatio(scheme);
            this.setNoErrorFlag(scheme);
            if (!scheme.errors.noErrors) {
                allValid = false;
            }
        }

        this.customColors.update((colors) => [...colors]);
        this.hasErrors.set(!allValid);
        return allValid;
    }

    cancel(): void {
        void this.reload();
        window.scrollTo(0, 0);
    }

    async save(): Promise<void> {
        if (!this.validate()) {
            this.message.set(
                "There was a problem saving your color scheme(s). Please resolve the errors below then save again.",
            );
            this.hasErrors.set(true);
            window.scrollTo(0, 0);
            return;
        }

        const allSchemes = [...this.customColors(), ...this.removedSchemes()];
        const payloadSchemes: SaveColorSchemePayload[] = allSchemes.map((scheme) => {
            return {
                is_new: !!scheme.isNew,
                is_removed: !!scheme.isRemoved,
                scheme_id: scheme.id,
                color_hash: scheme.hash,
                text_color: scheme.textColor,
                scheme_name: scheme.name ?? "",
                pattern: scheme.pattern?.name ?? null,
            };
        });

        try {
            const response = (await ItemColorMappingService.putColorSchemes(
                payloadSchemes as unknown as ColorScheme[],
            )) as SaveColorSchemeResponse;
            const savedSchemes = response?.schemes ?? [];
            this.hasErrors.set(false);
            this.message.set("Success!");

            this.customColors.update((colors) => {
                colors.forEach((colorScheme) => {
                    if (!colorScheme.isNew) return;
                    const savedColor = savedSchemes.find(
                        (savedScheme: SavedColorScheme) => savedScheme.scheme_name === colorScheme.name,
                    );
                    if (!savedColor) return;
                    colorScheme.id = savedColor.scheme_id;
                    colorScheme.isNew = false;
                });
                return [...colors];
            });

            window.scrollTo(0, 0);
        } catch (_error) {
            this.hasErrors.set(true);
            this.message.set("There was an error saving your color scheme(s).");
            window.scrollTo(0, 0);
        }
    }
}
